import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { useUserStore } from '../stores/user'

/**
 * Route-level permission requirements.
 * If a route has `meta.requiredPermission`, the user must have that permission.
 * If a route has `meta.requiredRoles`, the user must be one of those roles.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('../layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/DashboardView.vue'),
      },
      {
        path: 'components',
        name: 'Components',
        component: () => import('../views/ComponentsView.vue'),
        meta: { requiredPermission: 'components:read' },
      },
      {
        path: 'components/new',
        name: 'NewComponent',
        component: () => import('../views/ComponentsNewView.vue'),
        meta: { requiredPermission: 'components:write' },
      },
      {
        path: 'components/:id/edit',
        name: 'EditComponent',
        component: () => import('../views/ComponentEditView.vue'),
        props: true,
        meta: { requiredPermission: 'components:write' },
      },
      {
        path: 'inward',
        name: 'Inward',
        component: () => import('../views/InwardView.vue'),
        meta: { requiredPermission: 'stock:inward' },
      },
      {
        path: 'outward',
        name: 'Outward',
        component: () => import('../views/OutwardView.vue'),
        meta: { requiredPermission: 'stock:outward' },
      },
      {
        path: 'transactions',
        name: 'Transactions',
        component: () => import('../views/TransactionHistoryView.vue'),
        meta: { requiredPermission: 'components:read' },
      },
      {
        path: 'locations',
        name: 'Locations',
        component: () => import('../views/LocationsView.vue'),
      },
      {
        path: 'import',
        name: 'Import',
        component: () => import('../views/ImportView.vue'),
        meta: { requiredPermission: 'import:execute' },
      },
      {
        path: 'settings',
        component: () => import('../views/settings/SettingsLayout.vue'),
        children: [
          {
            path: '',
            redirect: '/settings/users',
          },
          {
            path: 'users',
            name: 'SettingsUsers',
            component: () => import('../views/settings/UsersView.vue'),
            meta: { requiredPermission: 'users:manage' },
          },
          {
            path: 'categories',
            name: 'SettingsCategories',
            component: () => import('../views/settings/CategoriesView.vue'),
            meta: { requiredPermission: 'categories:manage' },
          },
          {
            path: 'suppliers',
            name: 'SettingsSuppliers',
            component: () => import('../views/settings/SuppliersView.vue'),
            meta: { requiredPermission: 'suppliers:manage' },
          },
          {
            path: 'audit-log',
            name: 'SettingsAuditLog',
            component: () => import('../views/settings/AuditLogView.vue'),
            meta: { requiredPermission: 'audit:view' },
          },
        ],
      },
    ],
  },
]

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guard
router.beforeEach((to, _from, next) => {
  const userStore = useUserStore()

  // Auth check
  if (to.meta.requiresAuth && !userStore.isAuthenticated) {
    next('/login')
    return
  }
  if (to.path === '/login' && userStore.isAuthenticated) {
    next('/dashboard')
    return
  }

  // Permission check
  const requiredPermission = to.meta.requiredPermission as string | undefined
  if (requiredPermission && userStore.isAuthenticated) {
    if (!userStore.hasPermission(requiredPermission)) {
      // Redirect to dashboard with insufficient permissions
      next('/dashboard')
      return
    }
  }

  next()
})

export default router
